import { makeStyles } from "@mui/styles";
import React, { useEffect, useRef, useState } from "react";
import { isUserAuthorizedByServer } from "../api/userAccess";
import { logout } from "../util/authentication";
import { getAdminStation, getMultiStationAdminStationList, sortOnCaseSensitiveTexts } from "../util/commonUtils";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import PageLoading from "../components/PageLoading";
import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  STATION_FILTER_MUST_BE_SELECTED,
  STATUS_FILTER_MUST_BE_SELECTED,
} from "../util/messageConstants";
import {
  ALL_TOR_WOP_STATUSES,
  API_POLLING_INTERVAL,
  API_TIMEOUT,
  BREADCRUMBS_TITLE,
  HOME_PATH,
  MULTI_STATION_ADMIN_ACCESS,
  STATION,
  TIME_OFF_WOP_TYPES_BY_CONTRACT,
  WOP_AUDIT_CURRENT_LIST,
  WOP_AUDIT_DEFAULT_SELECTIONS,
} from "../util/constants";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import { Grid, InputAdornment, TextField } from "@mui/material";
import StarsSelectInput from "../components/fundamentals/StarsSelectInput";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { useDispatch, useSelector } from "react-redux";
import { getUnionContractGivenWorkgroup } from "../util/workgroupMapper";
import StarsDatePicker from "../components/fundamentals/StarsDatePicker";
import CancelIconButton from "../components/fundamentals/CancelIconButton";
import SearchButton from "../components/fundamentals/SearchButton";
import TimeOffRequestWOPTable from "../components/tor/TimeOffRequestWOPTable";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import {
  selectAdminTimeOffRequestWOPData,
  updateSearchString,
  updateSearchStringErrors,
  updateAdminTimeOffRequestFoundByReferenceNumberSearchIsLoading,
  updateAdminTimeOffRequestFoundByReferenceNumberSearch,
  updateAdminTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen,
  updateSelectedWorkgroup,
  updateSelectedDate,
  updateSelectedWopType,
  updateAvailableWopTypes,
} from "../slices/adminTimeOfRequest/adminTimeOffRequestWOPSlice";
import {
  fetchLatestTimeOffRequestWOPFreezeEvent,
  fetchTimeOffRequestSnapshotByReferenceNumber,
  fetchTimeOffRequestWOPEvents,
  fetchTimeOffRequestWOPAuditTimes,
  fetchTimeOffRequestWOPAuditEvent,
  fetchTimeOffRequestSnapshotByTorId,
} from "../api/timeOffRequest";
import { getScrubbedTimeOffRequestSnapshot, getScrubbedTimeOffRequestSnapshots } from "../util/timeOffRequestScrubber";
import { initializeWOPSearchIndex, searchTimeOffRequestsWOP } from "../util/searchTimeOffRequests";
import { timeOffRequestDataErrorCodeToErrorMessageMap } from "../util/errorMapper";
import { FIFTEEN_DAYS_IN_FUTURE, THREE_MONTHS_IN_PAST, TODAY, TOMORROW } from "../util/dateHelper";
import {
  convert_Luxon_To_DateFns,
  convert_Timestamp_To_Luxon_no_local_time,
  deserializeDateFromRedux,
  getFormattedTimeStamp,
  getIsoDateFormat,
  serializeDateToRedux,
} from "../util/dateTimeFormatter";
import { isLWOPTOWOPAllDayRequest, isLWOPTOWOPDayOfRequest } from "../util/timeOffRequestUtils";
import TimeOffRequestWOPFreeze from "../components/tor/TimeOffRequestWOPFreeze";
import { selectWebSocketEventData } from "../slices/webSocketEvent/webSocketEventSlice";
import { mergeWebSocketAdminTORs, mergeWebSocketFreezes } from "../util/webSocketUtils";
import { DateTime } from "luxon";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "auto",
    width: "100%",
  },
  pageTitle: {
    marginLeft: "2rem",
    fontSize: "24px",
    maxWidth: "90vw",
    textAlign: "left",
    marginTop: "10px",
  },
  inputRow: {
    width: "100%",
    display: "flex",
  },
  inputStation: {
    width: "5.2rem",
    marginRight: "1rem",
  },
  inputSelect: {
    width: "13rem",
    textAlign: "left",
  },
  inputSelectSection: {
    marginRight: "1rem",
  },
  auditSelectSection: {
    width: "100%",
    alignContent: "flex-end",
  },
  buttonsSection: {
    width: "100%",
    alignContent: "flex-end",
  },
  wopTableSection: {
    width: "100%",
    marginTop: "1rem",
  },
}));

const AdminTimeOffWOPPage = (props) => {
  //#region Constants
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const adminTimeOffRequestWOPData = useSelector(selectAdminTimeOffRequestWOPData);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [searchedTimeOffRequests, setSearchedTimeOffRequests] = useState([]);
  const [filteredTimeOffRequests, setFilteredTimeOffRequests] = useState([]);
  const [station, setStation] = useState("");
  const [isWOPListFrozen, setIsWOPListFrozen] = useState(false);
  const [wopFreezeData, setWopFreezeData] = useState(null);
  const [anyUnrankedEmployees, setAnyUnrankedEmployees] = useState(false);
  const [statusFilters, setStatusFilters] = useState(ALL_TOR_WOP_STATUSES);
  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.ADMIN_TIME_OFF_WOP];
  const breadcrumbsLinks = [HOME_PATH, props.featureConfig.adminPath];
  const torTableDesktopRef = useGridApiRef();
  const webSocketEventData = useSelector(selectWebSocketEventData);
  const wopWebsocketDataRef = useRef({});
  const freezeWebsocketDataRef = useRef({});
  const isAdminPage = true;

  const auditSelectionMap = useRef({});
  const [auditDateTimeSelections, setAuditDateTimeSelections] = useState(WOP_AUDIT_DEFAULT_SELECTIONS);
  const [currentAuditDateTimeSelection, setCurrentAuditDateTimeSelection] = useState(WOP_AUDIT_CURRENT_LIST);
  //#endregion

  //#region Hooks
  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      const station = getAdminStation(props.featureConfig.userStations, userSession, stationFilter);
      setStation(station);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    if (adminTimeOffRequestWOPData.selectedWorkgroup !== "") {
      const availableWOPTypes =
        TIME_OFF_WOP_TYPES_BY_CONTRACT[getUnionContractGivenWorkgroup(adminTimeOffRequestWOPData.selectedWorkgroup)];
      dispatch(updateAvailableWopTypes(availableWOPTypes));
      if (availableWOPTypes.includes(adminTimeOffRequestWOPData.selectedWopType)) {
        dispatch(updateSelectedWopType(adminTimeOffRequestWOPData.selectedWopType));
      } else {
        dispatch(updateSelectedWopType(""));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTimeOffRequestWOPData.selectedWorkgroup]);

  useEffect(() => {
    setStatusFilters(ALL_TOR_WOP_STATUSES);
    updateTimeOffRequestList();
    getLatestWOPFreezeEvent();
    getWopAuditTimes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adminTimeOffRequestWOPData.selectedWorkgroup,
    station,
    adminTimeOffRequestWOPData.selectedWopType,
    adminTimeOffRequestWOPData.selectedDate,
  ]);

  useEffect(() => {
    wopWebsocketDataRef.current = webSocketEventData.torAdminEvents;
    if (Object.values(webSocketEventData.torAdminEvents).length > 0) {
      const selectedDate = getSelectedDateSafely();
      if (
        selectedDate &&
        station &&
        adminTimeOffRequestWOPData.selectedWopType &&
        adminTimeOffRequestWOPData.selectedWorkgroup &&
        currentAuditDateTimeSelection === WOP_AUDIT_CURRENT_LIST
      ) {
        const torWebsocketIncomingEvents = mergeWebSocketAdminTORs(webSocketEventData.torAdminEvents, []);
        let torWebsocketEventsToProcess = torWebsocketIncomingEvents.filter(
          (torEvent) =>
            torEvent.station === station &&
            torEvent.timeOffRequestType === adminTimeOffRequestWOPData.selectedWopType &&
            torEvent.workgroup === adminTimeOffRequestWOPData.selectedWorkgroup &&
            (torEvent.shiftStart.toISODate() === selectedDate.toISODate() ||
              torEvent.shiftEnd.toISODate() === selectedDate.toISODate())
        );

        if (isWOPListFrozen) {
          torWebsocketEventsToProcess = torWebsocketEventsToProcess.filter((torEvent) =>
            timeOffRequests.some((timeOffRequest) => timeOffRequest.torId === torEvent.torId)
          );
        }

        torWebsocketEventsToProcess.forEach((torRequest) => {
          longPollTORSnapshotByTorId(torRequest.torId, torRequest.status);
        });
      }
      wopWebsocketDataRef.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.torAdminEvents]);

  useEffect(() => {
    freezeWebsocketDataRef.current = webSocketEventData.freezeEvents;
    if (Object.values(webSocketEventData.freezeEvents).length > 0) {
      const selectedDate = getSelectedDateSafely();
      if (
        selectedDate &&
        station &&
        adminTimeOffRequestWOPData.selectedWopType &&
        adminTimeOffRequestWOPData.selectedWorkgroup
      ) {
        setStatusFilters(ALL_TOR_WOP_STATUSES);
        const freezeWebsocketIncomingEvents = mergeWebSocketFreezes(
          webSocketEventData.freezeEvents,
          station,
          adminTimeOffRequestWOPData.selectedWopType,
          selectedDate.toISODate(),
          adminTimeOffRequestWOPData.selectedWorkgroup,
          []
        );

        freezeWebsocketIncomingEvents.forEach(() => {
          setCurrentAuditDateTimeSelection(WOP_AUDIT_CURRENT_LIST);
          getLatestWOPFreezeEvent();
          updateTimeOffRequestList();
        });
      }
      freezeWebsocketDataRef.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.freezeEvents]);
  //#endregion

  //#region API Calls
  const updateTimeOffRequestList = () => {
    const selectedDate = getSelectedDateSafely();
    if (
      station &&
      adminTimeOffRequestWOPData.selectedWorkgroup &&
      selectedDate &&
      adminTimeOffRequestWOPData.selectedWopType
    ) {
      setIsLoading(true);
      setIsError(false);
      setErrorObject({});
      setAnyUnrankedEmployees(false);
      fetchTimeOffRequestWOPEvents(
        station,
        adminTimeOffRequestWOPData.selectedWorkgroup,
        selectedDate.toISO(),
        adminTimeOffRequestWOPData.selectedWopType
      )
        .then((res) => res.json())
        .then((result) => {
          if (result.error) {
            setIsError(true);
            setErrorObject(timeOffRequestDataErrorCodeToErrorMessageMap.unexpectedTimeOffRequestDataError);
          } else {
            let scrubbedTimeOffRequests = getScrubbedTimeOffRequestSnapshots(result);
            const unrankedEmployees = scrubbedTimeOffRequests.some((tor) => tor.rank === "-"); // disable actions if there are any unranked employees
            setAnyUnrankedEmployees(unrankedEmployees);
            setTimeOffRequests(scrubbedTimeOffRequests);

            if (!isStatusSelectAllChecked()) {
              const filteredRequests = scrubbedTimeOffRequests.filter((tor) =>
                statusFilters.includes(tor.visibleStatus)
              );
              setFilteredTimeOffRequests(filteredRequests);
            }
          }
        })
        .catch((err) => {
          console.error("error happened!", err);
          setIsError(true);
          setErrorObject(timeOffRequestDataErrorCodeToErrorMessageMap.unexpectedTimeOffRequestDataError);
        })
        .finally(() => {
          setIsLoading(false);
          setCurrentAuditDateTimeSelection(WOP_AUDIT_CURRENT_LIST);
        });
    }
  };

  const getWopAuditTimes = () => {
    const selectedDate = getSelectedDateSafely();
    setIsError(false);
    setErrorObject({});
    setCurrentAuditDateTimeSelection(WOP_AUDIT_CURRENT_LIST);
    setAuditDateTimeSelections(WOP_AUDIT_DEFAULT_SELECTIONS);
    auditSelectionMap.current = {};
    if (
      station &&
      adminTimeOffRequestWOPData.selectedWorkgroup &&
      selectedDate &&
      adminTimeOffRequestWOPData.selectedWopType
    ) {
      const queryDate = getIsoDateFormat(selectedDate);
      let auditSelections = [...WOP_AUDIT_DEFAULT_SELECTIONS];
      fetchTimeOffRequestWOPAuditTimes(
        station,
        adminTimeOffRequestWOPData.selectedWorkgroup,
        queryDate,
        adminTimeOffRequestWOPData.selectedWopType
      )
        .then((res) => res.json())
        .then((result) => {
          const wopFreezeTimes = result.wopFreezeEvents;
          let isoString = "";
          wopFreezeTimes.forEach((timestamp) => {
            isoString = getFormattedTimeStamp(convert_Timestamp_To_Luxon_no_local_time(timestamp, station));
            auditSelectionMap.current[isoString] = timestamp;
            auditSelections.push(isoString);
          });
          setAuditDateTimeSelections(auditSelections);
        })
        .catch((err) => {
          console.error("Something went wrong");
          setIsError(true);
          setErrorObject(timeOffRequestDataErrorCodeToErrorMessageMap.unexpectedTimeOffRequestDataError);
        });
    }
  };

  const setAuditFreezeList = (isoCreatedAt) => {
    const createdAt = auditSelectionMap.current[isoCreatedAt];
    const selectedDate = getSelectedDateSafely();
    const queryDate = getIsoDateFormat(selectedDate);
    setIsLoading(true);
    fetchTimeOffRequestWOPAuditEvent(
      station,
      adminTimeOffRequestWOPData.selectedWorkgroup,
      queryDate,
      adminTimeOffRequestWOPData.selectedWopType,
      createdAt
    )
      .then((res) => res.json())
      .then((result) => {
        const auditEventList = result.wopFreezeEvent.wopRequests;
        let scrubbedAuditEventList = getScrubbedTimeOffRequestSnapshots(auditEventList);
        setWopFreezeData(result.wopFreezeEvent.metadata);
        setTimeOffRequests(scrubbedAuditEventList);
      })
      .catch((err) => {
        console.error("Something went wrong");
        setIsError(true);
        setErrorObject(timeOffRequestDataErrorCodeToErrorMessageMap.unexpectedTimeOffRequestDataError);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getLatestWOPFreezeEvent = () => {
    const selectedDate = getSelectedDateSafely();
    setIsWOPListFrozen(false);
    setWopFreezeData(null);
    if (
      station &&
      adminTimeOffRequestWOPData.selectedWorkgroup &&
      selectedDate &&
      adminTimeOffRequestWOPData.selectedWopType
    ) {
      fetchLatestTimeOffRequestWOPFreezeEvent(
        station,
        adminTimeOffRequestWOPData.selectedWorkgroup,
        getIsoDateFormat(selectedDate),
        adminTimeOffRequestWOPData.selectedWopType
      )
        .then((res) => res.json())
        .then((result) => {
          if (result.error) {
            console.error("Error fetching latest WOP freeze event:", result.error);
          } else if (result?.latestWopFreezeEvent) {
            setWopFreezeData(result.latestWopFreezeEvent);
            setIsWOPListFrozen(result.latestWopFreezeEvent.isFrozen);
          }
        })
        .catch((error) => {
          console.error("Error fetching latest WOP freeze event:", error);
        });
    }
  };

  // NOSONAR_BEGIN
  const longPollTORSnapshotByTorId = (torId, torStatus) => {
    const startTime = DateTime.now();
    function fetchTORSnapshot() {
      fetchTimeOffRequestSnapshotByTorId(torId, true)
        .then((response) => response.json())
        .then((result) => {
          if (result?.torSnapshotObject?.torId === torId && result?.torSnapshotObject?.status === torStatus) {
            updateTimeOffRequestList();
          } else if (!result || result.error) {
            console.error("Error longPollTORSnapshotByTorId event: ", result);
          } else {
            const currentTime = DateTime.now();
            const elapsedTime = currentTime - startTime;
            if (elapsedTime < API_TIMEOUT) {
              // recursive call till requestId matches or timeout happens
              setTimeout(fetchTORSnapshot, API_POLLING_INTERVAL);
            }
          }
        })
        .catch((error) => {
          console.error("Error longPollTORSnapshotByTorId event:", error);
        });
    }
    fetchTORSnapshot();
  };
  // NOSONAR_END
  //#endregion

  //#region Input Validations
  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const applySearch = (searchString, searchedTimeOffRequests) => {
    if (searchString === "") {
      setIsSearch(false);
      setSearchedTimeOffRequests(searchedTimeOffRequests);
      applyAdminTimeOffTableScroll();
      setIsLoading(false);
    } else {
      setIsSearch(true);
      setSearchedTimeOffRequests(searchedTimeOffRequests);
      applyAdminTimeOffTableScroll();
      setIsLoading(false);
    }
  };

  const applyAdminTimeOffTableScroll = () => {
    const currentDesktop = torTableDesktopRef.current;
    if (currentDesktop !== null && currentDesktop.getRowModels && currentDesktop.getRowModels().size > 0) {
      currentDesktop.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
    }
  };

  const getSelectedDateSafely = () => {
    // This safe function is used to be sure the default value is provided in the event the redux state is not available yet
    return adminTimeOffRequestWOPData.selectedDate
      ? deserializeDateFromRedux(adminTimeOffRequestWOPData.selectedDate)
      : null;
  };

  const disableFreezeButton = () => {
    const selectedDate = getSelectedDateSafely();
    return (
      anyUnrankedEmployees ||
      station === "" ||
      adminTimeOffRequestWOPData.selectedWorkgroup === "" ||
      adminTimeOffRequestWOPData.selectedWopType === "" ||
      selectedDate === null ||
      timeOffRequests.length === 0 ||
      currentAuditDateTimeSelection !== WOP_AUDIT_CURRENT_LIST ||
      (isLWOPTOWOPDayOfRequest(adminTimeOffRequestWOPData.selectedWopType) &&
        getIsoDateFormat(selectedDate) !== getIsoDateFormat(TODAY(station))) || //DAY OF - freeze for todays date only
      (isLWOPTOWOPAllDayRequest(adminTimeOffRequestWOPData.selectedWopType) &&
        getIsoDateFormat(selectedDate) !== getIsoDateFormat(TOMORROW(station))) //ALL DAY - freeze for tomorrow only
    );
  };

  const isAuditSelectionDisabled = () => {
    if (
      station &&
      adminTimeOffRequestWOPData.selectedWorkgroup &&
      adminTimeOffRequestWOPData.selectedDate &&
      adminTimeOffRequestWOPData.selectedWopType &&
      auditDateTimeSelections.length > 1 &&
      !isWOPListFrozen
    ) {
      return false;
    }
    return true;
  };
  //#endregion

  //#region Event Handlers
  const handleStationFilterChange = (event) => {
    setStation(event.target.value);
    dispatch(updateStationFilter(event.target.value));
    setCurrentAuditDateTimeSelection(WOP_AUDIT_CURRENT_LIST);
    setAnyUnrankedEmployees(false);
  };

  const handleWorkgroupInputChange = async (event) => {
    dispatch(updateSelectedWorkgroup(event.target.value));
    dispatch(updateSelectedWopType(""));
    setTimeOffRequests([]);
    setSearchedTimeOffRequests([]);
    setCurrentAuditDateTimeSelection(WOP_AUDIT_CURRENT_LIST);
    setAnyUnrankedEmployees(false);
  };

  const handleWOPTypeInputChange = async (event) => {
    dispatch(updateSelectedWopType(event.target.value));
    setTimeOffRequests([]);
    setSearchedTimeOffRequests([]);
    setCurrentAuditDateTimeSelection(WOP_AUDIT_CURRENT_LIST);
    setAnyUnrankedEmployees(false);

    if (isLWOPTOWOPDayOfRequest(event.target.value)) {
      dispatch(updateSelectedDate(serializeDateToRedux(TODAY(station))));
    } else {
      dispatch(updateSelectedDate(serializeDateToRedux(TOMORROW(station))));
    }
  };

  const handleDateChange = (date) => {
    dispatch(updateSelectedDate(serializeDateToRedux(date)));
    setTimeOffRequests([]);
    setSearchedTimeOffRequests([]);
    setCurrentAuditDateTimeSelection(WOP_AUDIT_CURRENT_LIST);
    setAnyUnrankedEmployees(false);
  };

  const handleStatusFilterChange = (event) => {
    let newStatusFilterSelectedList = event.target.value;
    setStatusFilters(newStatusFilterSelectedList);
    setIsLoading(true);
    const torRequestsToFilter = isSearch ? searchedTimeOffRequests : timeOffRequests;
    const filteredTors = torRequestsToFilter.filter((tor) => newStatusFilterSelectedList.includes(tor.visibleStatus));

    if (isSearch) {
      initializeWOPSearchIndex(filteredTors, isAdminPage);
      searchTimeOffRequestsWOP(adminTimeOffRequestWOPData.searchString, applySearch);
    }

    setFilteredTimeOffRequests(filteredTors);
    setIsLoading(false);
    applyAdminTimeOffTableScroll();
  };

  const isStatusSelectAllChecked = () => {
    return statusFilters.length === ALL_TOR_WOP_STATUSES.length;
  };

  const handleSearchChange = (event) => {
    dispatch(updateSearchString(event.target.value));
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(adminTimeOffRequestWOPData.searchString);
    }
  };

  const handleSearchCancel = () => {
    dispatch(updateSearchString(""));
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (newSearchString) => {
    const selectedDate = getSelectedDateSafely();
    if (
      station &&
      adminTimeOffRequestWOPData.selectedWorkgroup &&
      selectedDate &&
      adminTimeOffRequestWOPData.selectedWopType
    ) {
      dispatch(updateSearchStringErrors(""));
      const newSearchStringTrimmed = newSearchString.trim();
      if (
        !isNaN(parseInt(newSearchStringTrimmed)) &&
        parseInt(newSearchStringTrimmed) >= 10000000 &&
        parseInt(newSearchStringTrimmed) <= 99999999
      ) {
        dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsLoading(true));
        fetchTimeOffRequestSnapshotByReferenceNumber(newSearchStringTrimmed, userSession.eid, station, isAdminPage)
          .then((result) => {
            if (result.message) {
              console.error("Error fetching time off request snapshot by reference number:", result.message);
              dispatch(updateSearchStringErrors(result.message));
              dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
            } else {
              let adminTimeOffRequestSnapshotResult = result.torSnapshotObject;
              let scrubbedAdminTimeOffRequestSnapshot = getScrubbedTimeOffRequestSnapshot(
                adminTimeOffRequestSnapshotResult
              );
              if (
                scrubbedAdminTimeOffRequestSnapshot.timeOffRequestType === adminTimeOffRequestWOPData.selectedWopType
              ) {
                dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearch(adminTimeOffRequestSnapshotResult));
                dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
                applySearch(newSearchStringTrimmed, [scrubbedAdminTimeOffRequestSnapshot]);
                dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(true));
              } else {
                dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
                dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(true));
              }
            }
          })
          .catch((err) => {
            console.error("error happened!", err);
            dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
            dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(false));
          });
      } else {
        const torsToSearch = isStatusSelectAllChecked() ? timeOffRequests : filteredTimeOffRequests;
        initializeWOPSearchIndex(torsToSearch, isAdminPage);
        searchTimeOffRequestsWOP(newSearchStringTrimmed, applySearch);
      }
    }
  };

  const handleFreezeRefresh = () => {
    setStatusFilters(ALL_TOR_WOP_STATUSES);
    updateTimeOffRequestList();
    getLatestWOPFreezeEvent();
    getWopAuditTimes();
  };

  const sortAuditTimes = (a, b) => {
    // This will always cause "Current List" to come first, and sort from in descending order
    return b - a;
  };

  const handleAuditSelectionChange = (event) => {
    setStatusFilters(ALL_TOR_WOP_STATUSES);
    setCurrentAuditDateTimeSelection(event.target.value);
    if (event.target.value === WOP_AUDIT_CURRENT_LIST) {
      updateTimeOffRequestList();
    } else {
      setAuditFreezeList(event.target.value);
    }
  };
  //#endregion

  //#region Component Renders
  const renderAuditSelect = () => {
    return (
      <div>
        <StarsSelectInput
          disabled={isAuditSelectionDisabled()}
          label="You are viewing"
          menuMessage="Award Time"
          value={currentAuditDateTimeSelection}
          allOptions={auditDateTimeSelections}
          errors=""
          sortComparator={sortAuditTimes}
          onChange={(event) => handleAuditSelectionChange(event)}
          className={classes.inputSelect}
        />
      </div>
    );
  };

  const renderDesktopAdminTimeOffWOPPage = () => {
    return (
      <div className={classes.root}>
        <Grid>
          <StarsBreadcrumbs
            isConfirmationRequired={false}
            breadcrumbsLinks={breadcrumbsLinks}
            breadcrumbsList={breadcrumbsList}
          />
        </Grid>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item container justifyContent="flex-start" md={8} lg={8}>
            <Grid container className={classes.pageTitle}>
              LWOP/TOWOP Administration
            </Grid>
            <br />
          </Grid>
        </Grid>
        {userSession.isAdmin && (
          <Grid
            container
            sx={{
              pt: 3,
              display: "flex",
              justifyContent: "start",
              paddingTop: "10px",
              paddingLeft: "3rem",
              paddingRight: "3rem",
              paddingBottom: "10px",
            }}
            spacing={2}
          >
            <div className={classes.inputRow}>
              <div>
                <StarsSelectInput
                  label={STATION}
                  menuMessage={STATION}
                  value={stationFilter}
                  allOptions={getMultiStationAdminStationList(userSession)}
                  sortComparator={sortOnCaseSensitiveTexts}
                  onChange={(event) => handleStationFilterChange(event)}
                  errors={stationFilter === "" ? STATION_FILTER_MUST_BE_SELECTED : ""}
                  className={classes.inputStation}
                  isMenuItemDisabled={(station) => !props?.featureConfig?.userStations?.includes(station)}
                  disabled={userSession.appRole !== MULTI_STATION_ADMIN_ACCESS}
                />
              </div>
              <div className={classes.inputSelectSection}>
                <StarsSelectInput
                  label="Workgroup"
                  menuMessage="Workgroup"
                  value={adminTimeOffRequestWOPData.selectedWorkgroup}
                  allOptions={adminTimeOffRequestWOPData.availableWorkgroups}
                  errors=""
                  sortComparator={sortOnCaseSensitiveTexts}
                  onChange={(event) => handleWorkgroupInputChange(event)}
                  className={classes.inputSelect}
                />
              </div>
              <div className={classes.inputSelectSection}>
                <StarsSelectInput
                  disabled={adminTimeOffRequestWOPData.selectedWorkgroup === ""}
                  label="Type"
                  menuMessage="Type"
                  value={adminTimeOffRequestWOPData.selectedWopType}
                  allOptions={adminTimeOffRequestWOPData.availableWopTypes}
                  errors=""
                  sortComparator={sortOnCaseSensitiveTexts}
                  onChange={(event) => handleWOPTypeInputChange(event)}
                  className={classes.inputSelect}
                />
              </div>
              <div className={classes.inputSelectSection}>
                <StarsDatePicker
                  label="Date"
                  date={getSelectedDateSafely()}
                  onChange={handleDateChange}
                  dateErrors=""
                  className={classes.inputSelect}
                  minDate={convert_Luxon_To_DateFns(THREE_MONTHS_IN_PAST(station))}
                  maxDate={convert_Luxon_To_DateFns(FIFTEEN_DAYS_IN_FUTURE(station))}
                />
              </div>
              <div className={classes.inputSelectSection}>
                <StarsSelectInput
                  label="Status"
                  menuMessage="Filter on Status"
                  value={statusFilters}
                  allOptions={ALL_TOR_WOP_STATUSES}
                  sortComparator={sortOnCaseSensitiveTexts}
                  errors={statusFilters.length <= 0 ? STATUS_FILTER_MUST_BE_SELECTED : ""}
                  className={classes.inputSelect}
                  onChange={handleStatusFilterChange.bind(this)}
                  multiple={true}
                  fullWidth={true}
                />
              </div>
              <div className={classes.inputSelectSection}>
                <TextField
                  label="Search"
                  variant="outlined"
                  value={adminTimeOffRequestWOPData.searchString}
                  onChange={handleSearchChange.bind(this)}
                  onBlur={handleSearchBlur.bind(this)}
                  onKeyUp={handleSearchEnterKeyPressed.bind(this)}
                  className={classes.inputSelect}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {adminTimeOffRequestWOPData.searchString !== "" ? (
                          <CancelIconButton onSearchCancel={handleSearchCancel} />
                        ) : (
                          ""
                        )}
                        <SearchButton
                          onSearchSubmit={handleSearchSubmit}
                          searchString={adminTimeOffRequestWOPData.searchString}
                        />
                      </InputAdornment>
                    ),
                  }}
                  error={adminTimeOffRequestWOPData.searchStringErrors !== ""}
                  helperText={adminTimeOffRequestWOPData.searchStringErrors}
                />
              </div>
              <div className={classes.buttonsSection}>
                <TimeOffRequestWOPFreeze
                  freezeButtonDisabled={disableFreezeButton()}
                  station={station}
                  workgroup={adminTimeOffRequestWOPData.selectedWorkgroup}
                  wopType={adminTimeOffRequestWOPData.selectedWopType}
                  selectedDate={getSelectedDateSafely()}
                  isFrozen={isWOPListFrozen}
                  onFreezeRefresh={handleFreezeRefresh}
                />
              </div>
            </div>
            <div className={classes.wopTableSection}>
              <TimeOffRequestWOPTable
                timeOffRequests={
                  isSearch
                    ? searchedTimeOffRequests
                    : isStatusSelectAllChecked()
                    ? timeOffRequests
                    : filteredTimeOffRequests
                }
                wopType={adminTimeOffRequestWOPData.selectedWopType}
                anyUnrankedEmployees={anyUnrankedEmployees}
                selectedDate={getSelectedDateSafely()}
                isLoading={isLoading}
                isError={isError}
                errorObject={errorObject}
                station={station}
                torTableDesktopRef={torTableDesktopRef}
                isWOPListFrozen={isWOPListFrozen}
                wopFreezeData={wopFreezeData}
                isAdminPage={isAdminPage}
                isDesktopView={props.isDesktopView}
                renderAuditSelect={renderAuditSelect}
                currentAuditDateTimeSelection={currentAuditDateTimeSelection}
              />
            </div>
          </Grid>
        )}
      </div>
    );
  };

  const renderMobileAdminTimeOffWOPPage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div data-testid="admin-time-off-wop-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopAdminTimeOffWOPPage() : renderMobileAdminTimeOffWOPPage()}</>
      )}
    </div>
  );
  //#endregion
};

AdminTimeOffWOPPage.propTypes = {
  featureConfig: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default AdminTimeOffWOPPage;
